import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Home, ExternalLink, Building2, ArrowRight, FileText, ClipboardList, CalendarClock, Coins, Wrench, Activity, Sparkles, Droplets, Waves, Shield, Zap, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { PartnerShell } from "./Dashboard";

const AUD = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });
const LAB_LABEL = {
  pending_sample: "Awaiting sample", awaiting_drop_off: "Awaiting drop-off",
  received: "Received", results_entered: "Results entered",
  submitted_for_review: "In review", in_review: "In review",
  published: "Published", failed: "Failed",
};
const REPORT_LABEL = {
  awaiting_results: "Awaiting results", draft: "Draft",
  ready: "Ready", published: "Published", sent: "Sent",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); } catch { return "—"; }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

function fmtSpend(cents) {
  if (!cents || Number.isNaN(cents)) return "$0";
  return AUD.format(cents / 100);
}

function fmtSpendDollars(v) {
  if (v == null || Number.isNaN(v)) return "$0";
  return AUD.format(Number(v));
}

// Feb 2026 · Task 2 — recommended-next-action severity palette
const SEV = {
  green:  { ring: "ring-emerald-300", bg: "bg-emerald-50", chip: "bg-emerald-500", text: "text-emerald-900", dot: "🟢" },
  amber:  { ring: "ring-amber-300",   bg: "bg-amber-50",   chip: "bg-amber-500",   text: "text-amber-900",   dot: "🟡" },
  orange: { ring: "ring-orange-300",  bg: "bg-orange-50",  chip: "bg-orange-500",  text: "text-orange-900",  dot: "🟠" },
  red:    { ring: "ring-red-300",     bg: "bg-red-50",     chip: "bg-red-500",     text: "text-red-900",     dot: "🔴" },
};

// Feb 2026 · Task 1 — timeline event → icon + palette
const TL = {
  booking_created:                 { icon: Home,         color: "text-slate-600 bg-slate-100" },
  payment_received:                { icon: Coins,        color: "text-emerald-700 bg-emerald-100" },
  sample_kit_shipped:              { icon: ArrowRight,   color: "text-cyan-700 bg-cyan-100" },
  sample_kit_label_created:        { icon: ArrowRight,   color: "text-cyan-700 bg-cyan-50" },
  sample_collected:                { icon: Droplets,     color: "text-cyan-700 bg-cyan-100" },
  sample_received:                 { icon: Droplets,     color: "text-cyan-800 bg-cyan-100" },
  lab_started:                     { icon: Activity,     color: "text-indigo-700 bg-indigo-100" },
  results_entered:                 { icon: FileText,     color: "text-indigo-700 bg-indigo-100" },
  results_approved:                { icon: Sparkles,     color: "text-emerald-700 bg-emerald-100" },
  customer_emailed:                { icon: Mail,         color: "text-emerald-700 bg-emerald-100" },
  partner_notified_new_booking:    { icon: Mail,         color: "text-cyan-700 bg-cyan-100" },
  partner_notified_report:         { icon: Mail,         color: "text-cyan-700 bg-cyan-100" },
  partner_email_opened:            { icon: ExternalLink, color: "text-slate-700 bg-slate-100" },
  partner_email_clicked:           { icon: ExternalLink, color: "text-emerald-700 bg-emerald-100" },
  quote_requested:                 { icon: ClipboardList, color: "text-amber-700 bg-amber-100" },
  quote_contacted:                 { icon: Phone,        color: "text-amber-700 bg-amber-100" },
  quote_sent:                      { icon: FileText,     color: "text-indigo-700 bg-indigo-100" },
  quote_won:                       { icon: Sparkles,     color: "text-emerald-700 bg-emerald-100" },
  quote_lost:                      { icon: ArrowLeft,    color: "text-slate-500 bg-slate-100" },
  quote_completed:                 { icon: Sparkles,     color: "text-emerald-700 bg-emerald-100" },
  commission_paid:                 { icon: Coins,        color: "text-emerald-700 bg-emerald-100" },
  commission_accrued:              { icon: Coins,        color: "text-slate-600 bg-slate-100" },
  kit_reimbursed:                  { icon: Coins,        color: "text-emerald-700 bg-emerald-100" },
  retest_reminder_sent:            { icon: CalendarClock, color: "text-cyan-700 bg-cyan-100" },
  booking_cancelled:               { icon: ArrowLeft,    color: "text-rose-700 bg-rose-100" },
};

function TimelineIcon({ kind }) {
  const spec = TL[kind] || { icon: Activity, color: "text-slate-600 bg-slate-100" };
  const Ico = spec.icon;
  return (
    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${spec.color} shrink-0`}>
      <Ico className="w-4 h-4" />
    </span>
  );
}

export default function PartnerCustomerDetail() {
  const { customerKey } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        // F-1/Customer-Dossier — fetch the unified dossier so the
        // partner sees the full customer graph in one place.
        const r = await api.get(`/partner/customers/${encodeURIComponent(customerKey)}/dossier`);
        if (cancelled) return;
        setData(r.data);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Failed to load customer");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerKey]);

  if (loading) {
    return (
      <PartnerShell title="Customer" subtitle="Loading…">
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500 text-sm">
          Loading customer…
        </div>
      </PartnerShell>
    );
  }

  if (error || !data) {
    return (
      <PartnerShell title="Customer" subtitle="Record unavailable">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="partner-customer-error">
          <div className="text-base font-bold text-slate-900">Customer record unavailable</div>
          <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
            We couldn&apos;t load this customer record. The booking may not yet be linked to a
            customer profile, or you may not have permission to view it.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Link to="/partner/customers" data-testid="partner-customer-error-back"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-bold">
              <ArrowLeft className="w-4 h-4" /> Back to Customers
            </Link>
            <Link to="/partner/bookings" data-testid="partner-customer-error-bookings"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm font-bold hover:bg-slate-50">
              Open associated booking
            </Link>
            <a href={`mailto:support@awarewatertesting.com.au?subject=${encodeURIComponent(`Partner portal — customer record unavailable (${customerKey})`)}`}
              data-testid="partner-customer-error-support"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm font-bold hover:bg-slate-50">
              Report a problem
            </a>
          </div>
        </div>
      </PartnerShell>
    );
  }

  const c = data.customer || {};
  const totals = data.totals || {};
  const bookings = data.bookings || [];
  const properties = data.properties || [];
  const tests = data.tests || [];
  const reports = data.reports || [];
  const recs = data.recommendations || [];
  const quoteLeads = data.quote_leads || [];
  const commissions = data.commissions || [];
  const kitReimb = data.kit_reimbursements || [];
  const timeline = data.timeline || [];
  const nextAction = data.next_action || null;
  const lifetime = data.lifetime || null;
  const sev = SEV[nextAction?.severity] || SEV.green;

  return (
    <PartnerShell title={c.name || "Customer"} subtitle={c.suburb || ""}>
      <div className="space-y-6" data-testid="partner-customer-detail-page">
        <Link to="/partner/customers" className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-cyan-700" data-testid="partner-customer-back">
          <ArrowLeft className="w-4 h-4" /> Back to Customers
        </Link>

        {/* Contact block */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="partner-customer-contact-block">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Contact</div>
            <div className="text-slate-900 font-semibold">{c.name || "—"}</div>
            {c.email && (
              <a href={`mailto:${c.email}`} className="mt-1 flex items-center gap-1.5 text-sm text-slate-700 hover:text-cyan-700" data-testid="partner-customer-email">
                <Mail className="w-4 h-4" /> {c.email}
              </a>
            )}
            {c.phone ? (
              <a href={`tel:${c.phone}`} className="mt-1 flex items-center gap-1.5 text-sm text-slate-700 hover:text-cyan-700" data-testid="partner-customer-phone">
                <Phone className="w-4 h-4" /> {c.phone}
              </a>
            ) : (
              <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-500" data-testid="partner-customer-no-phone">
                <Phone className="w-4 h-4" /> No phone number provided
              </div>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Property</div>
            <div className="flex items-start gap-1.5 text-sm text-slate-700">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{c.address || "—"}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 text-xs px-2.5 py-1">
                <Building2 className="w-3.5 h-3.5" /> {totals.properties} propert{totals.properties === 1 ? "y" : "ies"}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 text-xs px-2.5 py-1">
                <Home className="w-3.5 h-3.5" /> {totals.bookings} booking{totals.bookings === 1 ? "" : "s"}
              </span>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Lifetime</div>
            <div className="text-3xl font-bold text-slate-900 tabular-nums" data-testid="partner-customer-lifetime">
              {fmtSpendDollars(totals.lifetime_spend_aud)}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              First booked {fmtDate(totals.first_booked_at)} · Last {fmtDate(totals.last_booked_at)}
            </div>
          </div>
        </div>

        {/* Recommended next action — Task 2 (Feb 2026 Partner CRM polish) */}
        {nextAction && (
          <div
            className={`rounded-2xl border ring-1 ${sev.ring} ${sev.bg} p-5 flex items-start gap-4`}
            data-testid="partner-customer-next-action"
          >
            <div className={`w-10 h-10 rounded-full ${sev.chip} text-white flex items-center justify-center shrink-0`}>
              <TrendingUp className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Recommended next action
              </div>
              <div className={`text-base font-bold ${sev.text}`}>
                <span className="mr-1">{sev.dot}</span>{nextAction.label}
              </div>
              {nextAction.why && (
                <div className="mt-1 text-sm text-slate-700">{nextAction.why}</div>
              )}
            </div>
            {nextAction.link && nextAction.cta && (
              <Link
                to={nextAction.link}
                className="inline-flex items-center gap-1 rounded-xl bg-slate-900 text-white text-xs font-semibold px-3 py-2 hover:bg-slate-800 shrink-0"
                data-testid="partner-customer-next-action-cta"
              >
                {nextAction.cta} <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        )}

        {/* Lifetime value — Task 4 (Feb 2026 Partner CRM polish) */}
        {lifetime && (
          <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="partner-customer-lifetime-panel">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-slate-500" />
              <div className="text-slate-900 font-semibold">Customer lifetime value</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <LtvTile label="Tests"           value={lifetime.tests} />
              <LtvTile label="Quote requests"  value={lifetime.quote_requests} />
              <LtvTile label="Recommendations" value={lifetime.recommendations} />
              <LtvTile label="Tank cleans"     value={lifetime.tank_cleans} icon={Droplets} />
              <LtvTile label="Filter installs" value={lifetime.filter_installs} icon={Shield} />
              <LtvTile label="UV installs"     value={lifetime.uv_installs} icon={Zap} />
              <LtvTile label="BioCleanse"      value={lifetime.biocleanse_jobs} icon={Waves} />
              <LtvTile
                label="Commission paid"
                value={fmtSpend(lifetime.commissions_paid_cents)}
                sub={lifetime.commissions_pending_cents ? `+ ${fmtSpend(lifetime.commissions_pending_cents)} pending` : null}
                icon={Coins} isCurrency
              />
              <LtvTile
                label="Kit reimbursement"
                value={fmtSpend(lifetime.kit_reimb_paid_cents)}
                sub={lifetime.kit_reimb_pending_cents ? `+ ${fmtSpend(lifetime.kit_reimb_pending_cents)} pending` : null}
                icon={Coins} isCurrency
              />
              <LtvTile
                label="First booking"
                value={fmtDate(lifetime.first_booked_at)}
                icon={CalendarClock}
                isText
              />
              <LtvTile
                label="Most recent"
                value={fmtDate(lifetime.last_booked_at)}
                icon={CalendarClock}
                isText
              />
            </div>
          </div>
        )}

        {/* Recommendations & Retest — surfaced from latest test so the
            partner never has to open the Summary PDF. */}
        {(recs.length > 0 || data.retest_at) && (
          <div className="rounded-2xl bg-cyan-50 border border-cyan-200 p-5" data-testid="partner-customer-recommendations">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-cyan-700" />
                <div className="text-sm font-bold uppercase tracking-wider text-cyan-900">Recommended follow-up</div>
              </div>
              {data.retest_at && (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-cyan-900">
                  <CalendarClock className="w-4 h-4" /> Retest by {fmtDate(data.retest_at)}
                </div>
              )}
            </div>
            {recs.length > 0 ? (
              <ul className="mt-3 space-y-2">
                {recs.slice(0, 8).map((r, i) => (
                  <li key={i} className="text-sm text-slate-800 flex items-start gap-2" data-testid={`partner-customer-rec-${i}`}>
                    <span className="text-cyan-700 font-bold mt-0.5">•</span>
                    <span>
                      <span className="font-semibold">{typeof r === "string" ? r : (r.title || r.name || r.action || r.service || "")}</span>
                      {typeof r === "object" && (r.why_recommended || r.why || r.description) && (
                        <span className="text-slate-600"> — {r.why_recommended || r.why || r.description}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-2 text-xs text-slate-600">No specific product actions — general consult.</div>
            )}
          </div>
        )}

        {/* Bookings timeline (with report + PDF links inline) */}
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="partner-customer-bookings">
          <div className="px-5 pt-4 pb-3 border-b border-slate-200 flex items-center gap-2">
            <Home className="w-4 h-4 text-slate-500" />
            <div className="text-slate-900 font-semibold">Booking history</div>
            <div className="text-xs text-slate-500 ml-auto">Most recent first</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left py-3 px-4">Reference</th>
                  <th className="text-left py-3 px-4">Date</th>
                  <th className="text-left py-3 px-4">Test</th>
                  <th className="text-right py-3 px-4">Amount</th>
                  <th className="text-left py-3 px-4">Lab</th>
                  <th className="text-left py-3 px-4">Report</th>
                  <th className="py-3 pr-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {bookings.map((b) => (
                  <tr key={b.reference} className="hover:bg-slate-50/60" data-testid={`partner-customer-booking-${b.reference}`}>
                    <td className="py-3 px-4 font-mono text-xs text-slate-700">{b.reference}</td>
                    <td className="py-3 px-4 text-slate-600">{fmtDate(b.created_at)}</td>
                    <td className="py-3 px-4 text-slate-700">{b.test_name || b.test_id || "—"}</td>
                    <td className="py-3 px-4 text-right tabular-nums">
                      {typeof b.amount === "number" ? fmtSpend(b.amount) : "—"}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex text-[11px] font-medium rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">
                        {LAB_LABEL[b.lab_status] || b.lab_status || "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex text-[11px] font-medium rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">
                        {REPORT_LABEL[b.report_status] || b.report_status || "—"}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-right whitespace-nowrap">
                      {(b.report_status === "published" || b.report_status === "sent") && (
                        <a
                          href={b.summary_pdf_url}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-lg bg-white border border-slate-300 text-slate-700 text-xs font-semibold px-2 py-1 hover:bg-slate-100 mr-2"
                          data-testid={`partner-customer-pdf-${b.reference}`}
                        >
                          <FileText className="w-3 h-3" /> PDF
                        </a>
                      )}
                      <Link
                        to={`/partner/bookings/${b.reference}`}
                        className="inline-flex items-center gap-1 rounded-lg bg-slate-900 text-white text-xs font-semibold px-2.5 py-1.5 hover:bg-slate-800"
                        data-testid={`partner-customer-open-${b.reference}`}
                      >
                        Open <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quote leads — customer follow-up status. */}
        {quoteLeads.length > 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="partner-customer-quote-leads">
            <div className="px-5 pt-4 pb-3 border-b border-slate-200 flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-slate-500" />
              <div className="text-slate-900 font-semibold">Quote requests</div>
              <div className="text-xs text-slate-500 ml-auto">{quoteLeads.length} total</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] text-sm">
                <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-3 px-4">Requested</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Booking</th>
                    <th className="text-left py-3 px-4">Package</th>
                    <th className="text-right py-3 px-4">Value</th>
                    <th className="py-3 pr-4"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {quoteLeads.map((ql) => (
                    <tr key={ql.id} className="hover:bg-slate-50/60" data-testid={`partner-customer-lead-${ql.id}`}>
                      <td className="py-3 px-4 text-slate-600">{fmtDate(ql.created_at)}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex text-[11px] font-medium rounded-full bg-cyan-100 text-cyan-800 px-2 py-0.5">
                          {ql.status || "quote_requested"}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-slate-700">{ql.proposal_reference || "—"}</td>
                      <td className="py-3 px-4 text-slate-700">{ql.package_name || "—"}</td>
                      <td className="py-3 px-4 text-right tabular-nums">
                        {ql.estimated_value_cents ? fmtSpend(ql.estimated_value_cents) : "—"}
                      </td>
                      <td className="py-3 pr-4 text-right">
                        <Link
                          to={`/partner/quote-leads`}
                          className="text-xs font-semibold text-cyan-700 hover:text-cyan-900"
                        >
                          Open →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Commissions + kit reimbursements */}
        {(commissions.length > 0 || kitReimb.length > 0) && (
          <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="partner-customer-commissions">
            <div className="flex items-center gap-2 mb-3">
              <Coins className="w-4 h-4 text-slate-500" />
              <div className="text-slate-900 font-semibold">Payments to you</div>
            </div>
            {commissions.length > 0 && (
              <div className="mb-4">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Commissions</div>
                <ul className="text-sm space-y-1">
                  {commissions.map((cx) => (
                    <li key={cx.id} className="flex items-center justify-between text-slate-700">
                      <span className="font-mono text-xs">{cx.booking_reference}</span>
                      <span className="text-slate-500 text-xs">{cx.status}</span>
                      <span className="tabular-nums font-semibold">{fmtSpend(cx.amount_cents)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {kitReimb.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Kit reimbursements</div>
                <ul className="text-sm space-y-1">
                  {kitReimb.map((kr) => (
                    <li key={kr.id} className="flex items-center justify-between text-slate-700">
                      <span className="font-mono text-xs">{kr.booking_reference}</span>
                      <span className="text-slate-500 text-xs">{kr.status}</span>
                      <span className="tabular-nums font-semibold">{fmtSpend(kr.amount_cents)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Properties CTA */}
        {properties.length > 0 && (
          <div className="rounded-2xl bg-cyan-50 border border-cyan-200 p-4 flex items-center justify-between" data-testid="partner-customer-passport-cta">
            <div className="text-sm text-slate-700">
              This customer has {properties.length} propert{properties.length === 1 ? "y" : "ies"} in their Water Passport history.
            </div>
            <Link
              to="/partner/passports"
              className="inline-flex items-center gap-1.5 rounded-xl bg-cyan-600 text-white text-xs font-semibold px-3 py-2 hover:bg-cyan-700"
              data-testid="partner-customer-view-passports"
            >
              View Passports <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        )}

        {/* Reports metadata for accountability */}
        {reports.length > 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="partner-customer-reports">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-slate-500" />
              <div className="text-slate-900 font-semibold">Report history ({tests.length} test{tests.length === 1 ? "" : "s"})</div>
            </div>
            <ul className="text-sm space-y-2">
              {reports.slice(0, 10).map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-700">
                  <span className="font-mono text-xs">{r.booking_reference}</span>
                  <span className="text-xs text-slate-500">v{r.version}</span>
                  <span className="text-xs">Sent {fmtDate(r.sent_at)}</span>
                  {r.partner_notify_sent_at && (
                    <span className="inline-flex text-[10px] font-semibold rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5">
                      You were notified {fmtDate(r.partner_notify_sent_at)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Timeline — Task 1 (Feb 2026 Partner CRM polish). Chronological
            activity feed the partner can scan without opening tabs. */}
        {timeline.length > 0 && (
          <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="partner-customer-timeline">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-4 h-4 text-slate-500" />
              <div className="text-slate-900 font-semibold">Customer timeline</div>
              <div className="text-xs text-slate-500 ml-auto">{timeline.length} events</div>
            </div>
            <ol className="relative">
              {/* Vertical rail */}
              <div className="absolute left-4 top-2 bottom-2 w-px bg-slate-200 pointer-events-none" />
              {timeline.slice(0, 40).map((e, i) => (
                <li key={i} className="relative flex items-start gap-3 pb-4" data-testid={`partner-customer-timeline-event-${i}`}>
                  <TimelineIcon kind={e.kind} />
                  <div className="flex-1 min-w-0 pt-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm text-slate-900 font-medium">{e.label}</span>
                      {e.booking_reference && (
                        <span className="text-[11px] font-mono text-slate-500">{e.booking_reference}</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {fmtDateTime(e.at)}
                      {e.actor && e.actor !== "system" && (
                        <span className="ml-2 inline-flex text-[10px] uppercase tracking-wider rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5">
                          {e.actor}
                        </span>
                      )}
                    </div>
                  </div>
                  {e.link && (
                    <Link to={e.link} className="text-xs font-semibold text-cyan-700 hover:text-cyan-900 pt-1">
                      Open →
                    </Link>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </PartnerShell>
  );
}

// Feb 2026 · Task 4 — compact lifetime tile
function LtvTile({ label, value, sub, icon: Icon, isCurrency = false, isText = false }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className={`${isText ? "text-sm" : isCurrency ? "text-xl" : "text-2xl"} font-bold text-slate-900 tabular-nums`}>
        {value ?? 0}
      </div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
